<template>
  <div class="w-full h-full">
    <div v-if="!embedded" class="flex items-center gap-3 mb-4">
      <Plus class="w-7 h-7 text-foreground" />
      <h1 class="text-2xl font-semibold tracking-tight">Create a new campaign</h1>
    </div>
    <Card class="mb-4">
      <CardHeader>
        <CardTitle>Name &amp; timeframe</CardTitle>
        <CardDescription>Give your campaign a name and choose when it runs.</CardDescription>
      </CardHeader>
      <CardContent>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="grid gap-1.5 sm:col-span-2">
            <label class="text-sm font-medium" for="campaign-name">Campaign name</label>
            <Input id="campaign-name" v-model="campaign.name" placeholder="Describe this campaign" />
          </div>
          <div class="grid gap-1.5">
            <label class="text-sm font-medium" for="campaign-start-date">Start date</label>
            <Input id="campaign-start-date" v-model="campaign.startDate" type="date" />
          </div>
          <div class="grid gap-1.5">
            <label class="text-sm font-medium" for="campaign-end-date">End date</label>
            <Input id="campaign-end-date" v-model="campaign.endDate" type="date" />
          </div>
        </div>
      </CardContent>
    </Card>
    <Card class="mb-4">
          <CardHeader>
            <CardTitle>Select campaign type</CardTitle>
            <CardDescription>Choose where this campaign appears and upload creative if required.</CardDescription>
          </CardHeader>
          <CardContent>
            <div class="grid gap-1.5">
              <label class="text-sm font-medium" for="campaign-slot">Campaign type</label>
              <Select v-model="campaign.slot">
                <SelectTrigger id="campaign-slot" class="w-full">
                  <SelectValue placeholder="Select a campaign type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="o in campaignType" :key="String(o.id)" :value="o.id">{{ o.text }}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div v-if="campaign.slot == 'TAKEOVER'" class="mt-4 rounded grid grid-cols-1 gap-4">
              <div class="col-span-1">
                <p
                  class="w-full mt-1 font-medium text-xs px-2 py-1.5 text-blue-800 bg-blue-50 border border-blue-300 dark:text-blue-300 dark:bg-blue-950/40 dark:border-blue-900/50 rounded-lg"
                >
                  Please upload a 1080x1080 pixel image to display as your ad creative for this
                  campaign.
                </p>
                <Input
                  class="w-full mt-2"
                  type="file"
                  placeholder="Boogi"
                  @change="uploadFile"
                  ref="fileInput"
                  accept=".png"
                />
                <p
                  v-if="errorMessage"
                  class="w-full mt-1 font-medium text-xs px-2 py-1.5 text-red-800 bg-red-50 border border-red-300 dark:text-red-300 dark:bg-red-950/40 dark:border-red-900/50 rounded-lg"
                >
                  {{ errorMessage }}
                </p>
              </div>
              <div class="col-span-2">
                <div>
                  <p class="font-semibold text-muted-foreground text-sm pb-1">Campaign asset:</p>
                  <div class="w-full">
                    <img
                      v-if="assetURL"
                      :src="assetURL"
                      class="w-full rounded-lg border border-border"
                    />
                    <div
                      v-else
                      class="w-full aspect-square rounded-lg border border-dashed border-border bg-muted flex items-center justify-center"
                    >
                      <span class="text-muted-foreground text-sm font-medium">1080 × 1080</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="campaign.slot == 'FEATURE'" class="mt-4 rounded grid grid-cols-1 gap-4">
              <div class="col-span-1">
                <p
                  class="w-full mt-1 font-medium text-xs px-2 py-1.5 text-blue-800 bg-blue-50 border border-blue-300 dark:text-blue-300 dark:bg-blue-950/40 dark:border-blue-900/50 rounded-lg"
                >
                  Please upload a 1280 x 720 pixel image to display as your ad creative for this
                  campaign.
                </p>
                <Input
                  class="w-full mt-2"
                  type="file"
                  placeholder="Boogi"
                  @change="uploadFile"
                  ref="fileInput"
                  accept=".png"
                />
                <p
                  v-if="errorMessage"
                  class="w-full mt-1 font-medium text-xs px-2 py-1.5 text-red-800 bg-red-50 border border-red-300 dark:text-red-300 dark:bg-red-950/40 dark:border-red-900/50 rounded-lg"
                >
                  {{ errorMessage }}
                </p>
              </div>
              <div class="col-span-2">
                <div>
                  <p class="font-semibold text-muted-foreground text-sm pb-1">Campaign asset:</p>
                  <div class="w-full">
                    <img
                      v-if="assetURL"
                      :src="assetURL"
                      class="w-full rounded-lg border border-border"
                    />
                    <div
                      v-else
                      class="w-full rounded-lg border border-dashed border-border bg-muted flex items-center justify-center"
                      style="aspect-ratio: 1280/780"
                    >
                      <span class="text-muted-foreground text-sm font-medium">1280 × 780</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
    <Card class="mb-4">
          <CardHeader>
            <CardTitle>Select relevant audiences</CardTitle>
            <CardDescription>Search and attach the audiences this campaign should target.</CardDescription>
          </CardHeader>
          <CardContent>
            <div :key="index">
              <div class="relative mb-4">
                <Search class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input v-model="audienceSearchInput" placeholder="Search for an audience" class="pl-9" />
              </div>
              <DataTable
                v-if="filteredAudiences.length > 0"
                :headers="headersTable1"
                :items="filteredAudiences"
                :loading="loading"
                v-model:server-options="serverOptions"
                :server-items-length="serverItemsLength"
                :hide-footer="true"
                :error="error"
                @retry="getAudiences(serverOptions.page - 1, serverOptions.rowsPerPage)"
              >
                <!-- Retailer Name -->
                <template #item-retailer="retailers">
                  <div class="flex items-center gap-2 text-xs">
                    <img v-if="retailers.retailer.logo" :src="retailers.retailer.logo" class="w-6 h-6 rounded object-contain border bg-white" alt="" />
                    <span class="text-sm font-medium truncate">{{ retailers.retailer.retailerName }}</span>
                  </div>
                </template>

                <!-- Retailer Website -->
                <template #item-website="retailers">
                  <span v-if="retailers.retailer.retailerWebsite" class="text-sm text-muted-foreground truncate">{{ retailers.retailer.retailerWebsite }}</span>
                  <span v-else class="text-muted-foreground">—</span>
                </template>

                <!-- Action Buttons -->
                <template #item-action="retailers">
                  <div class="flex justify-end items-center gap-1">
                    <Button
                      size="sm"
                      :disabled="isAudienceSelected(retailers.audience)"
                      @click="selectAudience(retailers.audience)"
                    >
                      <Plus class="w-4 h-4" />{{ isAudienceSelected(retailers.audience) ? 'Selected' : 'Select audience' }}
                    </Button>
                  </div>
                </template>
              </DataTable>

              <div v-if="selectedAudiences.length > 0" class="mt-4">
                <p class="text-sm font-medium mb-2">Selected audiences</p>
                <div
                  v-for="(audience, index) in selectedAudiences"
                  :key="index"
                  class="rounded-lg border bg-muted/40 px-3 py-2.5 mb-2"
                >
                  <div class="flex justify-between items-center">
                    <p class="text-sm font-medium truncate">{{ audience.name }}</p>
                    <Button
                      id="removeRetailerRule"
                      size="sm"
                      variant="ghost"
                      class="text-muted-foreground hover:text-destructive shrink-0"
                      @click="selectedAudiences.splice(index, 1)"
                    >
                      <Trash2 class="w-4 h-4" />Remove
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card class="mb-4">
          <CardHeader>
            <CardTitle>Select campaign offer</CardTitle>
            <CardDescription>Search for an activated offer to attach to this campaign.</CardDescription>
          </CardHeader>
          <CardContent>
            <div :key="index">
              <div class="relative mb-4">
                <Search class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input v-model="offerSearchInput" placeholder="Search by retailer name" class="pl-9" @input="onOfferSearchInput" />
              </div>
              <DataTable
                v-if="showResultsTableOffers"
                :headers="headersTable2"
                :items="retailers"
                :loading="loading"
                v-model:server-options="serverOptions"
                :server-items-length="serverItemsLength"
                :hide-footer="true"
                :error="error"
                @retry="getRetailerBySearch(serverOptions.page - 1, serverOptions.rowsPerPage, offerSearchInput)"
              >
                <template #item-retailer="retailers">
                  <div class="flex items-center gap-2">
                    <img v-if="retailers.retailer.logo" :src="retailers.retailer.logo" class="w-6 h-6 rounded object-contain border bg-white" alt="" />
                    <span class="text-sm font-medium truncate">{{ retailers.retailer.retailerName }}</span>
                  </div>
                </template>
                <template #item-supplier="retailers">
                  <span v-if="retailers.offer.supplier && retailers.offer.supplier.name" class="text-sm">{{ retailers.offer.supplier.name }}</span>
                  <span v-else class="text-muted-foreground">—</span>
                </template>
                <template #item-offertype="retailers">
                  <Badge variant="secondary">{{ formatOfferType(retailers.offer.offerType) }}</Badge>
                </template>
                <template #item-cashbackAmount="retailers">
                  <Badge variant="success">{{ retailers.cashbackEarnings[0].conditions[0].value.type === 'PERCENTAGE' ? '' : retailers.offer.currency }}{{ retailers.displayRules[0].highestCashbackPercentageAmount }}{{ retailers.cashbackEarnings[0].conditions[0].value.type === 'PERCENTAGE' ? '%' : '' }}</Badge>
                </template>
                <template #item-active="active">
                  <Badge v-if="active.active" variant="success">Live</Badge>
                  <Badge v-else variant="warning">Deactivated</Badge>
                </template>
                <template #item-action="retailers">
                  <div class="flex justify-end items-center gap-1">
                    <Button size="sm" @click="selectedOffer = retailers">Select Offer</Button>
                  </div>
                </template>
                <template #item-date="retailers">
                  <div class="text-right text-sm text-muted-foreground whitespace-nowrap">
                    {{ niceDateTime(retailers.createdOn) }}
                  </div>
                </template>
              </DataTable>
              <div v-if="selectedOffer != undefined" class="mt-4">
                <p class="text-sm font-medium mb-2">Selected offer</p>
                <div class="rounded-lg border bg-muted/40 px-3 py-2.5 mb-2">
                  <div class="flex justify-between items-center">
                    <div class="flex justify-start items-center gap-x-3 min-w-0">
                      <div class="flex items-center gap-2 text-sm min-w-0">
                        <img v-if="selectedOffer.retailer.logo" :src="selectedOffer.retailer.logo" class="w-6 h-6 rounded object-contain border bg-white shrink-0" alt="" />
                        <span class="text-sm font-medium truncate">{{ selectedOffer.retailer.retailerName }}</span>
                      </div>
                      <Badge variant="default">{{ selectedOffer.cashbackEarnings[0].conditions[0].value.type === 'PERCENTAGE' ? '' : selectedOffer.offer.currency }}{{ selectedOffer.displayRules[0].highestCashbackPercentageAmount }}{{ selectedOffer.cashbackEarnings[0].conditions[0].value.type === 'PERCENTAGE' ? '%' : '' }}</Badge>
                    </div>
                    <Button
                      id="removeRetailerRule"
                      size="sm"
                      variant="ghost"
                      class="text-muted-foreground hover:text-destructive shrink-0"
                      @click="selectedOffer = undefined"
                    >
                      <Trash2 class="w-4 h-4" />Remove
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

    <Button
      :disabled="!validCampaign"
      class="w-full"
      id="createContract"
      @click="campaign.slot == 'PROMOTED' ? createCampaign() : uploadCampaignPhoto()"
    >
      <WandSparkles class="w-4 h-4" />Create campaign
    </Button>
  </div>
</template>

<script setup></script>

<script>
import { DateTime } from 'luxon'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@boogi/ui'
import { Plus, Trash2, Search, WandSparkles } from 'lucide-vue-next'
import { DataTable } from '@boogi/ui'

export default {
  name: 'manage-publsihers',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    Button,
    Badge,
    Input,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    Plus,
    Trash2,
    Search,
    WandSparkles,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['created'],

  data() {
    return {
      serverOptions: {
        page: 1,
        rowsPerPage: 50,
      },
      campaignType: [
        { id: 'TAKEOVER', text: 'Homepage Takeover' },
        { id: 'FEATURE', text: 'Featured' },
        { id: 'PROMOTED', text: 'Promoted' },
      ],

      selectedAudiences: [],
      selectedOffer: undefined,
      showResultsTable: false,
      showResultsTableOffers: false,

      loading: false,
      error: false,
      headersTable1: [
        { text: 'Audience Name', value: 'audience.name' },
        { text: 'Audience Size', value: 'size' },
        { text: 'Action', value: 'action' },
      ],

      headersTable2: [
        { text: 'Retailer', value: 'retailer' },
        { text: 'Supplier', value: 'supplier' },
        { text: 'Type', value: 'offertype' },
        { text: 'Cashback', value: 'cashbackAmount' },
        { text: 'Status', value: 'active' },
        { text: 'Action', value: 'action' },
      ],
      retailers: [],
      audiences: [],
      errorMessage: '',
      asset: undefined,
      assetURL: undefined,

      campaign: {
        name: '',
        slot: undefined,
        startDate: undefined,
        endDate: undefined,
        associatedAudiences: undefined,
        assetLink: undefined,
        programId: undefined,
      },
      audienceSearchInput: '',
      offerSearchInput: '',
      offerSearchDebounce: null,
    }
  },
  created() {
    this.getAudiences(0, 50)
  },
  methods: {
    isAudienceSelected(audience) {
      return !!audience && this.selectedAudiences.some((a) => a.id === audience.id)
    },
    selectAudience(audience) {
      if (!audience || this.isAudienceSelected(audience)) return
      this.selectedAudiences.push({ ...audience })
    },
    onOfferSearchInput() {
      clearTimeout(this.offerSearchDebounce)
      const term = this.offerSearchInput
      if (!term || term.trim() === '') {
        this.retailers = []
        this.showResultsTableOffers = false
        return
      }
      this.offerSearchDebounce = setTimeout(() => {
        this.getRetailerBySearch(0, 50, term)
        this.showResultsTableOffers = true
      }, 300)
    },

    formatOfferType(t) {
      return ({ CARD_LINKED: 'Card-linked', AFFILIATE: 'Affiliate', IN_STORE: 'In-store', ONLINE: 'Online', PAYMENT: 'Payment-linked', CLICK_TO_ACTIVATE: 'Click to activate' })[t] || t
    },
    uploadFile: function (event) {
      this.asset = event.target.files[0]
      if (this.asset) {
        const img = new Image()
        img.onload = () => {
          if (this.campaign.slot === 'TAKEOVER') {
            if (img.width === 1080 && img.height === 1080) {
              this.assetURL = URL.createObjectURL(this.asset)
              this.errorMessage = '' // Clear any previous error message
            } else {
              this.asset = undefined
              this.assetURL = undefined
              this.errorMessage = 'Incorrect Image Size: Please upload a 1080x1080 pixel image.'
            }
          } else if (this.campaign.slot === 'FEATURE') {
            if (img.width === 1280 && img.height === 720) {
              this.assetURL = URL.createObjectURL(this.asset)
              this.errorMessage = '' // Clear any previous error message
            } else {
              this.asset = undefined
              this.assetURL = undefined
              this.errorMessage = 'Incorrect Image Size: Please upload a 1280x720 pixel image.'
            }
          }
        }
        img.src = URL.createObjectURL(this.asset)
      }
    },

    async uploadCampaignPhoto() {
      try {
        const formData = new FormData()
        formData.append('multipartFile', this.asset)

        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/campaign/upload-image`,
          {
            method: 'POST',
            body: formData,
            headers: {
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var dataResponse = await response.json()
          this.campaign.assetLink = dataResponse.assetURL
          this.createCampaign()
        }
      } catch (error) {
        return
      }
    },

    async createCampaign() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.campaign.associatedAudiences = this.selectedAudiences.map((val) => val.id)
        this.campaign.programId = this.selectedOffer.id

        const response = await this.$apiFetch(`${apiUrl}/private/api/client/campaign`, {
          method: 'POST',
          body: JSON.stringify(this.campaign),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          if (this.embedded) {
            this.$emit('created')
          } else {
            this.$router.push('/cashback-program/program-offers')
          }
        }
      } catch (error) {
        return
      }
    },

    async getAudiences(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/audience?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.serverItemsLength = object.totalElements
          this.audiences = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATE_SHORT)
    },

    async getRetailerBySearch(page, size, retailerSearch) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/retailer-search?page=${page}&size=${size}&search=${retailerSearch}&category=`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.retailers = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    filteredAudiences() {
      const term = (this.audienceSearchInput || '').trim().toLowerCase()
      if (!term) return this.audiences
      return this.audiences.filter((row) => {
        const name = row && row.audience && row.audience.name ? row.audience.name : ''
        return name.toLowerCase().includes(term)
      })
    },
    validCampaign() {
      if (this.campaign.slot == 'PROMOTED') {
        return (
          this.campaign.name != '' &&
          this.campaign.startDate != undefined &&
          this.campaign.endDate != undefined &&
          this.selectedAudiences.length > 0 &&
          this.selectedOffer != undefined
        )
      } else {
        return (
          this.campaign.name != '' &&
          this.campaign.startDate != undefined &&
          this.campaign.endDate != undefined &&
          this.selectedAudiences.length > 0 &&
          this.asset != undefined &&
          this.selectedOffer != undefined
        )
      }
    },
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getRetailers(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
